import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import LinkIcon from '@mui/icons-material/Link';

import {
  colors,
  primaryButtonSx,
  secondaryButtonSx,
  typo,
} from '../../theme/tokens';

import StatusChip from './StatusChip';
import WriteButton from './WriteButton';

/**
 * WU13 — ApprovalCard (Phase 5b).
 *
 * Approvals shipped in Phase 5b. Rendered on the workflow detail page when
 * a step is in `awaiting_approval` status. A different super-admin must
 * approve (four-eyes). `isFourEyesValid=false` disables Approve with an
 * inline alert. `busy=true` disables both buttons and shows a spinner.
 *
 * Reject requires a reason ≥ 20 chars.
 */
export interface ApprovalCardWorkflow {
  workflowUuid: string;
  id: number;
  tenantCode?: string | null;
  createdByEmail?: string | null;
  notes?: string | null;
}

export interface ApprovalCardPendingStep {
  env: string;
  stepOrder: number;
  status: string;
}

export interface ApprovalCardProps {
  workflow: ApprovalCardWorkflow;
  pendingStep: ApprovalCardPendingStep;
  /**
   * Whether the four-eyes requirement is satisfied (i.e. the approver is
   * NOT the workflow creator). Disables the Approve button when false.
   * Kept pure — do not AND in submission state; use `busy` for that.
   */
  isFourEyesValid: boolean;
  /** While true, disables Approve + Reject and shows a progress indicator. */
  busy?: boolean;
  onApprove: (workflowUuid: string) => void;
  onReject: (workflowUuid: string, reason: string) => void;
  /** Opens the lineage / audit drawer in the parent context. */
  onViewLineage?: (workflowUuid: string) => void;
}

export const MIN_REJECTION_REASON_LENGTH = 10;

const ApprovalCard: React.FC<ApprovalCardProps> = ({
  workflow,
  pendingStep,
  isFourEyesValid,
  busy = false,
  onApprove,
  onReject,
  onViewLineage,
}) => {
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const reasonValid = reason.trim().length >= MIN_REJECTION_REASON_LENGTH;

  const handleSubmitReject = () => {
    if (!reasonValid) return;
    onReject(workflow.workflowUuid, reason.trim());
  };

  return (
    <Paper
      elevation={0}
      data-testid="approval-card"
      sx={{
        p: 2.5,
        borderRadius: '12px',
        border: `1px solid ${colors.borderLight}`,
        borderLeft: `3px solid ${colors.approvalIcon}`,
        backgroundColor: colors.bgWhite,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1.5,
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography sx={{ ...typo.mono, fontWeight: 700, color: colors.textPrimary }}>
            W#{workflow.id}
          </Typography>
          {workflow.tenantCode && (
            <Typography sx={{ ...typo.smallText, color: colors.textSecondary, fontWeight: 600 }}>
              {workflow.tenantCode}
            </Typography>
          )}
          <Typography sx={{ ...typo.tinyText, color: colors.textTertiary }}>
            → {pendingStep.env.toUpperCase()}
          </Typography>
        </Box>
        <StatusChip status="awaiting_approval" label="Awaiting approval" />
      </Box>

      {/* Body */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {workflow.createdByEmail && (
          <Typography sx={typo.tinyText}>
            Created by <strong>{workflow.createdByEmail}</strong>
          </Typography>
        )}
        {workflow.notes && (
          <Typography sx={{ ...typo.tinyText, fontStyle: 'italic' }}>
            &ldquo;{workflow.notes}&rdquo;
          </Typography>
        )}
        {!isFourEyesValid && (
          <Alert severity="info" sx={{ py: 0, fontSize: 12 }} data-testid="four-eyes-alert">
            You created this workflow — a different super-admin must approve it (four-eyes rule).
          </Alert>
        )}
      </Box>

      {/* Reject reason input */}
      {rejecting && (
        <Box sx={{ mb: 2 }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why are you rejecting this workflow?"
            variant="outlined"
            size="small"
            inputProps={{ 'aria-label': 'Rejection reason' }}
            helperText={
              reasonValid
                ? ' '
                : reason.trim().length === 0
                  ? 'At least 10 characters'
                  : `${MIN_REJECTION_REASON_LENGTH - reason.trim().length} more characters required`
            }
          />
        </Box>
      )}

      {/* Actions */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {onViewLineage && (
          <Link
            component="button"
            onClick={() => onViewLineage(workflow.workflowUuid)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.25,
              fontSize: 12,
              fontWeight: 600,
              color: colors.textSecondary,
              textDecoration: 'none',
              fontFamily: 'Manrope, sans-serif',
              '&:hover': { color: colors.textPrimary, textDecoration: 'underline' },
            }}
          >
            <LinkIcon sx={{ fontSize: 14 }} />
            View lineage
          </Link>
        )}
        <Box sx={{ display: 'flex', gap: 1, ml: 'auto', alignItems: 'center' }}>
          {busy && <CircularProgress size={18} sx={{ color: colors.brand }} />}
          {rejecting ? (
            <>
              <Button
                size="small"
                variant="outlined"
                disabled={busy}
                onClick={() => {
                  setRejecting(false);
                  setReason('');
                }}
                sx={secondaryButtonSx}
              >
                Cancel
              </Button>
              <WriteButton
                size="small"
                variant="contained"
                color="error"
                disabled={!reasonValid || busy}
                startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                onClick={handleSubmitReject}
                sx={{
                  ...primaryButtonSx,
                  backgroundColor: colors.errorIcon,
                  '&:hover': { backgroundColor: colors.errorIcon },
                  color: '#fff',
                }}
              >
                Reject
              </WriteButton>
            </>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                startIcon={<CancelIcon sx={{ fontSize: 16 }} />}
                disabled={busy}
                onClick={() => setRejecting(true)}
                sx={secondaryButtonSx}
                aria-label="Reject workflow"
              >
                Reject
              </Button>
              <WriteButton
                size="small"
                variant="contained"
                disabled={!isFourEyesValid || busy}
                startIcon={<CheckCircleOutlineIcon sx={{ fontSize: 16 }} />}
                onClick={() => onApprove(workflow.workflowUuid)}
                sx={primaryButtonSx}
                aria-label="Approve workflow"
              >
                Approve
              </WriteButton>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default ApprovalCard;
